import {
  useGenerateOrderSlip,
  usePostOrder,
} from '@api/pos/pos-transaction-api.rtk';
import useAppContext from '@hooks/useAppContext';
import { ConfirmationDialog } from '@hooks/useConfirmationDialog';
import useDisclosure from '@hooks/useDisclosure';
import useToast from '@hooks/useToast';
import {
  selectPOSHasSpecificDiscount,
  selectPOSIsAllHasDiscount,
  selectPOSShiftID,
  selectPOSShiftRole,
  selectPOSstationID,
  selectPOSTableDetails,
  selectPOSTableOrders,
  selectPOSTableTransactionID,
} from '@modules/point-of-sale/slice/pos-selector';
import { Button, Stack } from '@mui/material';
import { useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import { buildPosOrderPayload } from '@utils/pos-utils';
import { hashSignature } from '@utils/security';
import { useMemo, useState } from 'react';
import POSBillOutModal from './pos-bill-out.modal';
import {
  usePOSOrderDetailsCtx,
  usePOSWindowDrawerCtx,
} from './pos-order-taking-window';

/**
 * POSActionButtons component displays action buttons for the POS order-taking window.
 * It includes buttons for applying discounts, billing out, and other actions.
 *
 * @returns {JSX.Element} The rendered component.
 */
const POSActionButtons = () => {
  const { discountModalDisclosure, othersDrawerDisclosure, selectedItems } =
    usePOSWindowDrawerCtx();
  const {
    // startLoadingStatus,
    stopLoadingStatus,
  } = useAppContext();
  const { isBillOut } = usePOSOrderDetailsCtx();
  const tableDetails = useAppSelector(selectPOSTableDetails);
  const orders = useAppSelector(selectPOSTableOrders);
  const hasShiftID = useAppSelector(selectPOSShiftID);
  const transactionID = useAppSelector(selectPOSTableTransactionID);
  const propertyID = useAppSelector(selectPropertyID);
  const stationID = useAppSelector(selectPOSstationID);
  const shiftRole = useAppSelector(selectPOSShiftRole);
  const { toast } = useToast();
  const billOutDisclosure = useDisclosure();

  const isWaiter = shiftRole === 'Waiter';

  const [postOrder] = usePostOrder();
  const [generateOrderSlip] = useGenerateOrderSlip();

  const hasSpecificDiscount = useAppSelector(selectPOSHasSpecificDiscount);
  const isAllHasDiscount = useAppSelector(selectPOSIsAllHasDiscount);

  const { isSelectedHasDiscount, isAllSelectedHasDiscount } = useMemo(() => {
    const isSelectedHasDiscount =
      selectedItems?.length > 0 &&
      selectedItems?.some((item) => item.discountID);

    const isAllSelectedHasDiscount =
      selectedItems?.length > 0 &&
      selectedItems?.every((item) => item.discountID);

    return { isSelectedHasDiscount, isAllSelectedHasDiscount };
  }, [selectedItems]);

  // Check if all items in the order are printed
  const [showDialog, setShowDialog] = useState(false);
  const { hasNewItem, noOrders } = useMemo(() => {
    const hasNewItem = orders?.some(
      (item) => !item.orderID || !item.isPrintedOS,
    );

    const noOrders = orders?.length <= 0;

    return { hasNewItem, noOrders };
  }, [orders]);

  const handleSyncOrder = async () => {
    try {
      // startLoadingStatus(`Syncing ${orders.length > 0 ? 'Orders' : 'Order'}`);

      await postOrder({
        transactionID,
        payload: {
          properties: [
            {
              property: {
                propertyID,
                posOrders: buildPosOrderPayload({
                  orders:
                    orders?.filter(
                      (item) => !item.isPrintedOS && !item.orderID,
                    ) ?? [],
                  propertyID,
                  stationID,
                }),
              },
            },
          ],
        },
      }).unwrap();

      // startLoadingStatus('Generating Order Slip');

      await generateOrderSlip({
        transactionID,
        payload: {
          properties: [
            {
              property: {
                propertyID,
                signature: hashSignature(String(propertyID)),
              },
            },
          ],
        },
      }).unwrap();
    } catch (error) {
      toast({ message: error, severity: 'warning' });
    } finally {
      stopLoadingStatus();
      billOutDisclosure.onOpen();
      setShowDialog(false);
    }
  };

  return (
    <>
      {!isBillOut && (
        <Stack
          direction='row'
          sx={{
            gap: 2,
            py: 2,
            px: 2.5,
            pt: 0,
            button: {
              minHeight: '60px',
              width: '100%',
              borderRadius: '8px',
              boxShadow: 1,
            },
          }}
        >
          {/* Button for other actions */}
          <Button
            variant='contained'
            onClick={othersDrawerDisclosure.onOpen}
            sx={{ bgcolor: '#DFE3E8', color: '#000' }}
          >
            Others
          </Button>

          {/* Button to open the discount modal */}
          <Button
            variant='contained'
            onClick={() => {
              if (hasSpecificDiscount && isSelectedHasDiscount) {
                toast({
                  message: 'Some selected item(s) already have a discount',
                  severity: 'warning',
                });
                return;
              }

              if (tableDetails?.isGroupComputation) {
                toast({
                  message: 'This transaction already has a discount',
                  severity: 'warning',
                });
                return;
              }

              if (
                isAllHasDiscount ||
                (hasSpecificDiscount && isAllSelectedHasDiscount)
              ) {
                toast({
                  message: 'All items already has a discount',
                  severity: 'warning',
                });
                return;
              }

              if (hasSpecificDiscount && selectedItems.length <= 0) {
                toast({
                  message: 'Select an item to add a discount',
                  severity: 'warning',
                });
                return;
              }

              discountModalDisclosure.onOpen();
            }}
            disabled={noOrders}
            sx={{ bgcolor: '#DFE3E8', color: '#000' }}
          >
            Apply Discount
          </Button>

          {/* Button to bill out; enabled only if all items are printed */}
          <Button
            variant='contained'
            sx={{ bgcolor: 'error.main' }}
            disabled={noOrders || isWaiter}
            onClick={
              hasNewItem
                ? () => setShowDialog(true)
                : !hasShiftID
                  ? () =>
                      toast({
                        severity: 'warning',
                        message:
                          'Only cashiers are allowed to process bill outs.',
                      })
                  : billOutDisclosure.onOpen
            }
          >
            Bill Out
          </Button>
        </Stack>
      )}

      <POSBillOutModal {...{ modalDisclosure: billOutDisclosure }} />

      <ConfirmationDialog
        dialogStatus='warning'
        iconColor='warning'
        open={showDialog}
        title={`Pending OS for Printing`}
        desc={`Do you want to proceed and send the unprinted orders to their station?`}
        onConfirm={handleSyncOrder}
        onClose={() => setShowDialog(false)}
      />
    </>
  );
};

export default POSActionButtons;
